import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab
} from '@mui/material';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Warning as WarningIcon,
  Add as AddIcon
} from '@mui/icons-material';
import axios from 'axios';
import { differenceInCalendarDays, format, isToday } from 'date-fns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export default function KanbanBoard() {
  const [kanbanTasks, setKanbanTasks] = useState({
    todo: [],
    inProgress: [],
    done: []
  });
  const [draggedTask, setDraggedTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [companyConfig, setCompanyConfig] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    dueDate: '',
    tags: '',
    estimatedHours: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Fetch current user
  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/current`, {
        withCredentials: true
      });

      if (!response.data || !response.data.employee_id) {
        throw new Error('Employee ID not found in user data');
      }

      setEmployeeId(response.data.employee_id);
      return response.data.employee_id;
    } catch (err) {
      //console.error("Error fetching current user:", err);
      setError('Failed to fetch user information');
      return null;
    }
  };

  // Fetch todos from backend
  const fetchTodos = async (userId) => {
    try {
       const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/todos?createdBy=${userId}`, {
        withCredentials: true,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      

      if (response.data.success) {
        const todos = response.data.data;
        
        // Group todos by status
        const groupedTodos = {
          todo: todos.filter(todo => todo.status === 'todo'),
          inProgress: todos.filter(todo => todo.status === 'inProgress'),
          done: todos.filter(todo => todo.status === 'done')
        };

        setKanbanTasks(groupedTodos);
      }
    } catch (err) {
      //console.error("Error fetching todos:", err);
      setError('Failed to fetch todos');
    } finally {
      setLoading(false);
    }
  };

  // Create new todo
  const createTodo = async (todoData) => {
    try {
       const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.post(
        `${API_URL}/api/todos`,
        todoData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      //console.error("Error creating todo:", err);
      throw err;
    }
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    
    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    }

    if (!formData.dueDate) {
    errors.dueDate = 'Due date is required';
  }


    
    if (formData.estimatedHours && (isNaN(formData.estimatedHours) || formData.estimatedHours < 0)) {
      errors.estimatedHours = 'Estimated hours must be a positive number';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    setSubmitting(true);
    try {
      const todoData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        dueDate: formData.dueDate || null,
        createdBy: employeeId,
        tags: formData.tags ? formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag) : [],
        estimatedHours: formData.estimatedHours ? parseFloat(formData.estimatedHours) : null
      };

      const newTodo = await createTodo(todoData);
      
      // Add to local state
      setKanbanTasks(prev => ({
        ...prev,
        todo: [newTodo, ...prev.todo]
      }));
      
      // Reset form and close dialog
      setFormData({
        title: '',
        description: '',
        priority: 'medium',
        dueDate: '',
        tags: '',
        estimatedHours: ''
      });
      setOpenDialog(false);
      
    } catch (err) {
      setError('Failed to create todo');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: '',
      tags: '',
      estimatedHours: ''
    });
    setFormErrors({});
  };
  useEffect(() => {
    const initializeData = async () => {
      const userId = await fetchCurrentUser();
      if (userId) {
        await fetchTodos(userId);
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          if (payload.company_id) {
            const res = await axios.get(`${API_URL}/api/cmp/${payload.company_id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (res.data && res.data.data) {
              setCompanyConfig(res.data.data);
            }
          }
        } catch (e) {
          // console.error("Error fetching company config", e);
        }
      }
    };
    initializeData();
  }, []);

  // Update todo status
  const updateTodoStatus = async (todoId, newStatus) => {
    try {
       const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.put(
        `${API_URL}/api/todos/${todoId}/status`,
        { status: newStatus   },{
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      if (response.data.success) {
        return response.data.data;
      }
    } catch (err) {
      //console.error("Error updating todo status:", err);
      setError('Failed to update todo status');
      throw err;
    }
  };

  // Delete todo
  const deleteTodo = async (todoId) => {
    try {
       const token = localStorage.getItem("token"); // get JWT token
    if (!token) {
      alert("No authentication token found. Please log in.");
      return;
    }
  
    const payload = JSON.parse(atob(token.split('.')[1]));
    const company_id = payload.company_id;
      const response = await axios.delete(`${API_URL}/api/todos/${todoId}`,  {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });

      if (response.data.success) {
        // Remove from local state
        setKanbanTasks(prev => ({
          todo: prev.todo.filter(task => task.id !== todoId),
          inProgress: prev.inProgress.filter(task => task.id !== todoId),
          done: prev.done.filter(task => task.id !== todoId)
        }));
      }
    } catch (err) {
      //console.error("Error deleting todo:", err);
      setError('Failed to delete todo');
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, task, sourceColumn) => {
    setDraggedTask({ task, sourceColumn });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetColumn) => {
    e.preventDefault();
    if (!draggedTask) return;

    const { task, sourceColumn } = draggedTask;
    if (sourceColumn === targetColumn) return;

    try {
      // Update status in backend
      await updateTodoStatus(task.id, targetColumn);

      // Update local state
      setKanbanTasks(prev => ({
        ...prev,
        [sourceColumn]: prev[sourceColumn].filter(t => t.id !== task.id),
        [targetColumn]: [...prev[targetColumn], { ...task, status: targetColumn }]
      }));
    } catch (err) {
      // Handle error - task stays in original column
      //console.error("Failed to update task status");
    }

    setDraggedTask(null);
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return '#f44336';
      case 'high': return '#ff9800';
      case 'medium': return '#2196f3';
      case 'low': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  // Get due date status color
  const getDueDateColor = (task) => {
     if (task.status === 'done') return '#9e9e9e';
    switch (task.dueDateStatus) {
      case 'overdue': return '#f44336';
      case 'due-today': return '#ff9800';
      case 'due-soon': return '#ff5722';
      case 'on-track': return '#4caf50';
      case 'completed': return '#9e9e9e';
      default: return '#9e9e9e';
    }
  };


  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = {};
    if (companyConfig?.timezone) {
      options.timeZone = companyConfig.timezone;
    }
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  const getDueDateLabel = (dueDate) => {
  const today = new Date();
  const due = new Date(dueDate);
  const diff = differenceInCalendarDays(due, today);

  if (isToday(due)) {
    return 'Do it today';
  } else if (diff > 0) {
    return `${diff} day${diff > 1 ? 's' : ''} remaining`;
  } else {
    return `Due ${Math.abs(diff)} day${Math.abs(diff) > 1 ? 's' : ''} ago`;
  }
};

  if (loading) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '450px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper elevation={3} sx={{ p: 3, height: '450px' }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Paper elevation={3} sx={{ p: 3, height: '450px',overflow:"auto"}}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center' }}>
          <DragIcon sx={{ mr: 1, color: '#ff9800' }} />
          To-Do-List Board
        </Typography>
        
        <Grid container spacing={2} sx={{ height: '380px' }}>
          {Object.entries(kanbanTasks).map(([columnName, tasks]) => (
            <Grid item xs={4} key={columnName}>
              <Paper
                sx={{
                  p: 2,
                  height: '100%',
                  backgroundColor: columnName === 'todo' ? '#fff3e0' :
                                 columnName === 'inProgress' ? '#e3f2fd' : '#e8f5e8',
                  overflow: 'auto'
                }}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, columnName)}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2, textTransform: 'capitalize' }}>
                  {columnName === 'inProgress' ? 'In Progress' : columnName} ({tasks.length})
                </Typography>
                
                {tasks.map((task) => (
                  <Card
                    key={task.id}
                    sx={{ 
                      mb: 1, 
                      cursor: 'grab', 
                      '&:hover': { boxShadow: 4 },
                      opacity: draggedTask?.task.id === task.id ? 0.5 : 1
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, task, columnName)}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', flex: 1 }}>
                          {task.title}
                        </Typography>
                        <Tooltip title="Delete task">
                          <IconButton 
                            size="small" 
                            sx={{ ml: 1, p: 0.5 }}
                            onClick={() => deleteTodo(task.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      {/* {task.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {task.description}
                        </Typography>
                      )} */}
                      
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                        {/* Priority chip */}
                        <Chip
                          size="small"
                          label={task.priority}
                          variant='outlined'
                          // icon={<FlagIcon />}
                          sx={{
                            borderColor: getPriorityColor(task.priority),
      color: getPriorityColor(task.priority),
                            fontSize: '0.65rem',
                            height: '20px'
                          }}
                        />
                        
                        {/* Due date chip */}
                        {task.dueDate && (
                                <Chip
                                  size="small"
                                  label={getDueDateLabel(task.dueDate)}
                                  variant='outlined'
                                  // icon={task.isOverdue ? <WarningIcon /> : <ScheduleIcon />}
                                   sx={{
      borderColor: getDueDateColor(task),
      color: getDueDateColor(task),
      fontSize: '0.65rem',
      height: '20px'
    }}
                                />
                              )}

                      </Box>
                      
                   
                 
                    </CardContent>
                  </Card>
                ))}
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        aria-label="add"
        sx={{
          position: 'absolute',
          bottom: 16,
          right: 16,
        }}
        onClick={() => setOpenDialog(true)}
      >
        <AddIcon />
      </Fab>

      {/* Create Todo Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Todo</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Title"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              error={!!formErrors.title}
              helperText={formErrors.title}
              margin="normal"
              required
            />
         
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                label="Priority"
                onChange={(e) => handleInputChange('priority', e.target.value)}
              >
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="medium">Medium</MenuItem>
                <MenuItem value="high">High</MenuItem>
                <MenuItem value="urgent">Urgent</MenuItem>
              </Select>
            </FormControl>
  
         <TextField
  fullWidth
  required
  margin="normal"
  label="Due Date"
  type="date"
  value={formData.dueDate}
  onChange={(e) => handleInputChange('dueDate', e.target.value)}
  InputLabelProps={{
    shrink: true, // keep label visible
  }}
  inputProps={{
    min: new Date().toISOString().split('T')[0], // disable past dates
     onKeyDown: (e) => e.preventDefault(), // prevent manual typing
  }}
  error={!!formErrors.dueDate}
  helperText={formErrors.dueDate}
/>



            
           
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? <CircularProgress size={20} /> : 'Create Todo'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}